import { useState } from "react";
import { Star, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { usePendingRatings, PendingRating } from "@/hooks/usePendingRatings";
import { format } from "date-fns";

const STAR_LABELS = ["", "Poor", "Fair", "Good", "Very Good", "Excellent"];
const TIP_PERCENTAGES = [10, 15, 20, 25];

const RatingStars = ({
  rating,
  hoveredRating,
  onRate,
  onHover,
  onLeave,
}: {
  rating: number;
  hoveredRating: number;
  onRate: (r: number) => void;
  onHover: (r: number) => void;
  onLeave: () => void;
}) => (
  <div className="flex gap-3 justify-center">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        onClick={() => onRate(star)}
        onMouseEnter={() => onHover(star)}
        onMouseLeave={onLeave}
        className="transition-transform hover:scale-125 active:scale-95"
      >
        <Star
          className={`h-12 w-12 sm:h-14 sm:w-14 ${
            star <= (hoveredRating || rating)
              ? "fill-yellow-500 text-yellow-500"
              : "text-muted-foreground/40"
          }`}
        />
      </button>
    ))}
  </div>
);

const RatingCard = ({
  pending,
  onSubmitted,
}: {
  pending: PendingRating;
  onSubmitted: () => void;
}) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState("");
  const [showComment, setShowComment] = useState(false);
  const [selectedTip, setSelectedTip] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (rating === 0) return;
    setLoading(true);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      // Insert session_rating (the gate)
      const { error: ratingError } = await supabase
        .from("session_ratings" as any)
        .insert({
          booking_id: pending.bookingId,
          rater_id: session.user.id,
          rated_id: pending.otherPersonId,
          rater_role: pending.raterRole,
          rating,
          comment: comment.trim() || null,
        } as any);

      if (ratingError) throw ratingError;

      // For learners: also insert into reviews table for public coach ratings
      if (pending.raterRole === "learner") {
        const tipAmount = selectedTip
          ? (pending.sessionPrice * selectedTip) / 100
          : 0;

        await supabase.from("reviews").insert({
          booking_id: pending.bookingId,
          coach_id: pending.coachId,
          learner_id: session.user.id,
          rating,
          comment: comment.trim() || null,
          tip_amount: tipAmount,
          tip_percentage: selectedTip,
        });

        // Process tip if selected
        if (selectedTip && tipAmount > 0) {
          const { data: tipData, error: tipError } = await supabase
            .from("tips")
            .insert({
              booking_id: pending.bookingId,
              learner_id: session.user.id,
              coach_id: pending.coachId,
              amount: tipAmount,
              percentage: selectedTip,
              base_price: pending.sessionPrice,
              status: "pending",
            })
            .select()
            .single();

          if (!tipError && tipData) {
            const { data: paymentData } = await supabase.functions.invoke(
              "create-tip-payment",
              { body: { tipId: tipData.id, tipAmount } }
            );

            if (paymentData?.paymentIntentId) {
              await supabase
                .from("tips")
                .update({
                  status: "completed",
                  stripe_payment_intent_id: paymentData.paymentIntentId,
                })
                .eq("id", tipData.id);

              await supabase.functions.invoke("process-tip-payout", {
                body: { tipId: tipData.id },
              });
            }
          }
        }
      }

      toast({
        title: "Rating submitted",
        description: "Thanks for your feedback!",
      });

      onSubmitted();
    } catch (error: any) {
      console.error("Error submitting rating:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-2xl shadow-xl p-6 sm:p-8 w-full max-w-md mx-4 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground">
          How was your session?
        </h2>
        <p className="text-muted-foreground">
          Rate your experience with{" "}
          <span className="font-semibold text-foreground">
            {pending.otherPersonName}
          </span>
        </p>
        <p className="text-sm text-muted-foreground">
          {pending.experienceTitle} •{" "}
          {format(new Date(pending.sessionDate), "MMM d, yyyy")}
        </p>
      </div>

      {/* Stars */}
      <div className="space-y-2">
        <RatingStars
          rating={rating}
          hoveredRating={hoveredRating}
          onRate={setRating}
          onHover={setHoveredRating}
          onLeave={() => setHoveredRating(0)}
        />
        <p className="text-center text-sm font-medium text-muted-foreground h-5">
          {STAR_LABELS[hoveredRating || rating] || "Tap to rate"}
        </p>
      </div>

      {/* Comment toggle */}
      {!showComment ? (
        <button
          type="button"
          onClick={() => setShowComment(true)}
          className="flex items-center gap-2 text-sm text-primary hover:underline mx-auto"
        >
          <MessageSquare className="h-4 w-4" />
          Add a comment
        </button>
      ) : (
        <Textarea
          placeholder="Share your thoughts (optional)..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          maxLength={300}
          autoFocus
        />
      )}

      {/* Tip options for learners */}
      {pending.raterRole === "learner" && (
        <div className="space-y-3">
          <p className="text-sm font-medium text-center text-muted-foreground">
            Add a tip? (optional)
          </p>
          <div className="grid grid-cols-4 gap-2">
            {TIP_PERCENTAGES.map((pct) => {
              const tipAmt = (pending.sessionPrice * pct) / 100;
              return (
                <Button
                  key={pct}
                  type="button"
                  variant={selectedTip === pct ? "default" : "outline"}
                  size="sm"
                  onClick={() =>
                    setSelectedTip(selectedTip === pct ? null : pct)
                  }
                  className="flex flex-col h-auto py-2"
                >
                  <span className="font-bold">{pct}%</span>
                  <span className="text-xs">${tipAmt.toFixed(2)}</span>
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Submit */}
      <Button
        onClick={handleSubmit}
        disabled={rating === 0 || loading}
        className="w-full h-12 text-base font-semibold"
      >
        {loading ? "Submitting..." : "Submit Rating"}
      </Button>

      <p className="text-xs text-center text-muted-foreground">
        You must rate this session before continuing
      </p>
    </div>
  );
};

export const MandatoryRatingOverlay = () => {
  const { pendingRatings, loading, refetch } = usePendingRatings();

  if (loading || pendingRatings.length === 0) return null;

  // Show the first pending rating
  const current = pendingRatings[0];

  return (
    <div className="fixed inset-0 z-[9999] bg-background/80 backdrop-blur-sm flex items-center justify-center">
      <RatingCard pending={current} onSubmitted={refetch} />
    </div>
  );
};
